import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import API from '../utils/api';
import { StatCard, PageLoader } from '../components/common/UI';
import { formatCurrency, todayShamsi, daysUntil } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import { MdWarning, MdClose } from 'react-icons/md';
import { 
  FaFolderOpen, FaCheckCircle, FaRegUserCircle, FaMoneyBillWave, FaCreditCard, FaChartLine 
} from 'react-icons/fa';
import { MdVideoCameraBack, MdNotifications, MdCalendarToday, MdAlarm, MdPeople, MdAssignment, MdRemoveRedEye } from 'react-icons/md'

const CHART_COLORS = ['#3366ff', '#1a4ef5', '#6090ff', '#0f2fb3', '#818cf8', '#93c5fd'];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentProjects, setRecentProjects] = useState([]);
  const [projectReport, setProjectReport] = useState(null);
  const [deadlineAlerts, setDeadlineAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const [showAlertPanel, setShowAlertPanel] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [dashRes, projRes, projectsRes] = await Promise.all([
          API.get('/reports/dashboard'),
          API.get('/reports/projects').catch(() => ({ data: null })),
          API.get('/projects'),
        ]);
        
        setStats(dashRes.data);
        setProjectReport(projRes.data);
        
        const projectsData = Array.isArray(projectsRes.data) ? projectsRes.data : [];
        setRecentProjects(projectsData.slice(0, 6));

        const alerts = [];
        projectsData.forEach(p => {
          if (p.status === 'Delivered') return;
          const days = daysUntil(p.deliveryDate);
          if (days === 1) {
            alerts.push({ id: `proj-${p._id}-1`, type: 'critical', icon: '🚨', text: `پروژه "${p.name}" فردا تحویل دارد. اقدام فوری لازم است.` });
          } else if (days === 0) {
            alerts.push({ id: `proj-${p._id}-0`, type: 'critical', icon: '⏰', text: `پروژه "${p.name}" امروز باید تحویل شود!` });
          } else if (days < 0) {
            alerts.push({ id: `proj-${p._id}-late`, type: 'danger', icon: '❌', text: `پروژه "${p.name}" ${Math.abs(days)} روز تأخیر دارد!` });
          } else if (days <= 3) {
            alerts.push({ id: `proj-${p._id}-soon`, type: 'warning', icon: '⚠️', text: `پروژه "${p.name}" تا ${days} روز دیگر تحویل دارد.` });
          }
        });
        setDeadlineAlerts(alerts);
      } catch (err) {
        console.error('Error loading dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <PageLoader />;

  const visibleAlerts = deadlineAlerts.filter(a => !dismissedAlerts.has(a.id));

  const projectStatusData = projectReport?.byStatus?.map(s => ({
    name: statusLabel(s._id), value: s.count
  })) || [];

  const projectTypeData = projectReport?.byType?.map(t => ({
    name: typeLabel(t._id), value: t.count
  })) || [];

  const alertBg = { 
    critical: 'border-red-200 bg-red-100', 
    danger: 'border-red-200 bg-red-50', 
    warning: 'border-yellow-200 bg-yellow-50' 
  };
  const alertText = { 
    critical: 'text-red-700', 
    danger: 'text-red-600', 
    warning: 'text-yellow-700' 
  };

  return (
    <div className="space-y-5 fade-in">
      {/* Header */}
      <div className="flex items-start justify-between relative">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-xl">
            <FaRegUserCircle size={32} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">سلام، {user?.name}</h1>
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <MdCalendarToday size={14} />
              <span>{todayShamsi()}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAlertPanel(!showAlertPanel)}
            className="relative p-2 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-200"
          >
            <MdNotifications size={22} className="text-gray-600" />
            {visibleAlerts.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-bold border border-gray-300">
                {visibleAlerts.length > 9 ? '9+' : visibleAlerts.length}
              </span>
            )}
          </button>
          
          <Link to="/projects" className="hidden sm:flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-medium transition-colors">
            + پروژه جدید
          </Link>
        </div>

        {showAlertPanel && visibleAlerts.length > 0 && (
          <div className="absolute top-14 left-0 right-0 md:left-auto md:right-0 md:w-96 z-50 glass-card border border-gray-200 rounded-2xl shadow-2xl animate-slideDown">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-gray-800 font-bold flex items-center gap-2">
                <MdNotifications size={20} />
                اعلان‌ها ({visibleAlerts.length})
              </h3>
              <button onClick={() => setShowAlertPanel(false)} className="text-gray-500 hover:text-gray-800 transition-colors">
                <MdClose size={18} />
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {visibleAlerts.map(alert => (
                <div key={alert.id} className={`p-4 border-b border-gray-100 ${alertBg[alert.type]} hover:bg-gray-50 transition-colors`}>
                  <div className="flex items-start gap-3">
                    <span className="text-xl flex-shrink-0">{alert.icon}</span>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${alertText[alert.type]}`}>{alert.text}</p>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-gray-600">
                          {alert.type === 'critical' ? '🔴 فوری' : alert.type === 'danger' ? '🟠 تأخیر' : '🟡 هشدار'}
                        </span>
                        <button
                          onClick={() => setDismissedAlerts(prev => new Set([...prev, alert.id]))}
                          className="text-gray-600 hover:text-gray-600 text-xs px-2 py-1 rounded-lg hover:bg-gray-100"
                        >
                          بستن ✕
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {visibleAlerts.length > 1 && (
              <div className="p-3 border-t border-gray-200 text-center">
                <button
                  onClick={() => setDismissedAlerts(new Set(deadlineAlerts.map(a => a.id)))}
                  className="text-xs text-blue-600 hover:text-blue-700 transition-colors"
                >
                  بستن همه اعلان‌ها
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<FaFolderOpen className="text-blue-600" />} label="پروژه‌های فعال" value={stats?.projects?.active || 0} sub={`از ${stats?.projects?.total || 0} کل`} color="blue" onClick={() => navigate('/projects')} />
        <StatCard icon={<FaCheckCircle className="text-blue-600" />} label="پروژه‌های تکمیل" value={stats?.projects?.completed || 0} color="green" onClick={() => navigate('/projects')} />
        <StatCard icon={<MdVideoCameraBack className="text-blue-600" />} label="ویدیوهای در ادیت" value={stats?.videos?.editing || 0} sub={`${stats?.videos?.review || 0} در بررسی`} color="purple" onClick={() => navigate('/videos')} />
        <StatCard icon={<MdWarning className="text-blue-600" />} label="هشدارهای جدید" value={stats?.alerts || 0} color={stats?.alerts > 0 ? 'red' : 'blue'} onClick={() => navigate('/alerts')} />
      </div>

      {/* Finance Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard icon={<FaMoneyBillWave className="text-green-600" />} label="درآمد کل" value={formatCurrency(stats?.finance?.income)} color="green" onClick={() => navigate('/finance')} />
        <StatCard icon={<FaCreditCard className="text-red-600" />} label="مصارف کل" value={formatCurrency(stats?.finance?.expense)} color="red" onClick={() => navigate('/finance')} />
        <StatCard icon={<FaChartLine className="text-blue-600" />} label="سود خالص" value={formatCurrency(stats?.finance?.profit)} color={stats?.finance?.profit >= 0 ? 'green' : 'red'} onClick={() => navigate('/finance')} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {projectStatusData.length > 0 && (
          <div className="glass-card p-5">
            <h3 className="text-gray-800 font-semibold mb-4">وضعیت پروژه‌ها</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={projectStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                  {projectStatusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '10px', color: '#1e293b', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {projectStatusData.map((item, i) => (
                <span key={i} className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  {item.name} ({item.value})
                </span>
              ))}
            </div>
          </div>
        )}

        {projectTypeData.length > 0 && (
          <div className="glass-card p-5">
            <h3 className="text-gray-800 font-semibold mb-4">نوع پروژه‌ها</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={projectTypeData} barSize={28}>
                <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '10px', color: '#1e293b', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Bar dataKey="value" fill="#3366ff" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Tasks & Employees stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<MdAssignment className="text-yellow-600 text-lg" />} label="تسک‌های در انتظار" value={stats?.tasks?.pending || 0} color="yellow" onClick={() => navigate('/tasks')} />
        <StatCard icon={<MdWarning className="text-red-600 text-lg" />} label="تسک‌های تأخیر" value={stats?.tasks?.overdue || 0} color={stats?.tasks?.overdue > 0 ? 'red' : 'blue'} onClick={() => navigate('/tasks')} />
        <StatCard icon={<MdPeople className="text-blue-600 text-lg" />} label="کارمندان فعال" value={stats?.employees?.total || 0} color="blue" onClick={() => navigate('/employees')} />
        <StatCard icon={<MdRemoveRedEye className="text-purple-600 text-lg" />} label="ویدیوهای منتظر تأیید" value={stats?.videos?.review || 0} color="purple" onClick={() => navigate('/videos')} />
      </div>

      {/* Recent Projects */}
      {recentProjects.length > 0 && (
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-gray-800 font-semibold">آخرین پروژه‌ها</h3>
            <Link to="/projects" className="text-blue-600 text-sm hover:text-blue-700">مشاهده همه ←</Link>
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="premium-table">
              <thead>
                <tr>
                  <th>نام پروژه</th><th>نوع</th><th>مشتری</th><th>وضعیت</th><th>مبلغ کل</th>
                </tr>
              </thead>
              <tbody>
                {recentProjects.map(p => (
                  <tr key={p._id}>
                    <td className="font-medium">{p.name}</td>
                    <td className="text-gray-500">{categoryLabel(p)}</td>
                    <td className="text-gray-500">{p.client?.name || '---'}</td>
                    <td><StatusBadgeSmall status={p.status} /></td>
                    <td className="text-green-600">{formatCurrency(p.totalAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-3">
            {recentProjects.map(p => (
              <div key={p._id} className="mobile-card">
                <div className="flex items-start justify-between mb-2">
                  <span className="font-medium text-gray-800">{p.name}</span>
                  <StatusBadgeSmall status={p.status} />
                </div>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{p.client?.name}</span>
                  <span className="text-green-600">{formatCurrency(p.totalAmount)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function statusLabel(s) {
  const m = { 
    Planned: 'برنامه‌ریزی', 
    'Shooting Day': 'شوتینگ', 
    'Shooting Done': 'شوتینگ تمام', 
    Editing: 'ادیت', 
    Review: 'بررسی', 
    Delivered: 'تحویل' 
  };
  return m[s] || s;
}

function typeLabel(t) {
  const m = { 
    Wedding: 'عروسی', 
    Party: 'پارتی', 
    Commercial: 'تبلیغ', 
    Music: 'موزیک', 
    Documentary: 'مستند', 
    Other: 'دیگر' 
  };
  return m[t] || t;
}

function categoryLabel(p) {
  const m = {
    wedding: 'مراسم عروسی',
    funeral: 'مراسم فاتحه',
    otherEvent: 'سایر مراسم',
    poster: 'دیزاین پوستر',
    commercial: 'تبلیغاتی',
    corporate: 'شرکتی',
    documentary: 'مستند',
  };
  return m[p.mainCategory] || typeLabel(p.type);
}

function StatusBadgeSmall({ status }) {
  const colors = {
    Planned: 'bg-slate-100 text-slate-700',
    'Shooting Day': 'bg-orange-100 text-orange-700',
    'Shooting Done': 'bg-yellow-100 text-yellow-700',
    Editing: 'bg-blue-100 text-blue-700',
    Review: 'bg-purple-100 text-purple-700',
    Delivered: 'bg-green-100 text-green-700'
  };
  return <span className={`badge ${colors[status] || 'bg-gray-500/20 text-gray-600'}`}>{statusLabel(status)}</span>;
}